import { useEffect, useState, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import ConfirmDialog from "../components/ConfirmDialog";
import api from "../services/api";
import toast from "react-hot-toast";
import {
  ROLE_ADMIN,
  ROLE_ENVIRONMENTAL_DISTRICT_OFFICER,
  ROLE_REVIEWER,
  ROLE_VIEWER,
  DISTRICT_EDO_ROLES,
  normalizeRole,
} from "../constants/roles";

export default function ManageRoles() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: ROLE_ENVIRONMENTAL_DISTRICT_OFFICER,
    district: ""
  });

  const token = localStorage.getItem("token");

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get("/users", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.data || []);
    } catch (err) {
      console.error("Fetch users error:", err);
      alert("Failed to fetch users");
      setUsers([]);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Add new user
  const handleSubmit = async (e) => {
    e.preventDefault();
    const toastId = toast.loading("Adding user...");
    setLoading(true);

    try {
      const res = await api.post(
        "/users/add",
        {
          name: form.name,
          email: form.email.toLowerCase(),
          password: form.password,
          role: form.role,
          district: DISTRICT_EDO_ROLES.includes(form.role) ? form.district : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        setUsers(prev => [res.data.user, ...prev]);
        setForm({ name: "", email: "", password: "", role: ROLE_ENVIRONMENTAL_DISTRICT_OFFICER, district: "" });
        toast.success("User added successfully ", { id: toastId });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add user ❌", { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  // Update user role
  const updateRole = useCallback(async (id, role) => {
    const toastId = toast.loading("Updating role...");
    try {
      const res = await api.put(
        `/users/${id}/role`,
        { role },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        setUsers(prev => prev.map(u => (u.id === id ? { ...u, role } : u)));
        toast.success("Role updated successfully ", { id: toastId });
      }
    } catch (err) {
      console.error("Update role error:", err);
      toast.error(err.response?.data?.message || "Failed to update role ❌", { id: toastId });
    }
  }, [token]);

// Delete user
const deleteUser = useCallback(async (user) => {
  setUserToDelete(user);
}, []);

const confirmDeleteUser = useCallback(async () => {
  if (!userToDelete) return;
  const toastId = toast.loading("Deleting user...");
  try {
    await api.delete(`/users/${userToDelete.id}`);
    setUsers(prev => prev.filter((existingUser) => existingUser.id !== userToDelete.id));
    toast.success("User deleted successfully", { id: toastId });
  } catch (err) {
    console.error("Delete user error:", err);
    toast.error(err.response?.data?.message || "Failed to delete user ❌", { id: toastId });
  } finally {
    setUserToDelete(null);
  }
}, [userToDelete]);

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const districtCount = users.filter((u) =>
    DISTRICT_EDO_ROLES.includes(normalizeRole(u.role)),
  ).length;

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />

      <main className="flex-1 p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md mb-6">
          <h2 className="text-2xl font-bold">Manage Users & Roles</h2>
          <p className="text-sm mt-1">Add, edit, and manage system users</p>
        </div>

        {/* Stats */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-xl shadow border-l-4 border-green-600">
            <p className="text-xs text-gray-500">Total Users</p>
            <h3 className="text-2xl font-bold text-green-700">{users.length}</h3>
          </div>
          <div className="bg-white p-4 rounded-xl shadow border-l-4 border-blue-600">
            <p className="text-xs text-gray-500">Admins</p>
            <h3 className="text-2xl font-bold text-blue-700">
              {users.filter((u) => u.role === "admin").length}
            </h3>
          </div>
          <div className="bg-white p-4 rounded-xl shadow border-l-4 border-purple-600">
            <p className="text-xs text-gray-500">ESMP Writers</p>
            <h3 className="text-2xl font-bold text-purple-700">{districtCount}</h3>
          </div>
          <div className="bg-white p-4 rounded-xl shadow border-l-4 border-purple-600">
            <p className="text-xs text-gray-500">Reviewers</p>
            <h3 className="text-2xl font-bold text-purple-700">
              {users.filter((u) => u.role === "reviewer").length}
            </h3>
          </div>
          <div className="bg-white p-4 rounded-xl shadow border-l-4 border-cyan-600">
            <p className="text-xs text-gray-500">Viewers</p>
            <h3 className="text-2xl font-bold text-cyan-700">
              {users.filter((u) => u.role === "viewer").length}
            </h3>
          </div>
        </div>

        {/* Add User Form */}
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl shadow mb-6">
          <h3 className="text-lg font-semibold mb-4 text-green-700">Add New User</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <input type="text" placeholder="Full Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="border p-2 rounded focus:ring-2 focus:ring-green-600 outline-none" required />
            <input type="email" placeholder="Email Address" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="border p-2 rounded focus:ring-2 focus:ring-green-600 outline-none" required />
            <input type="password" placeholder="Password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="border p-2 rounded focus:ring-2 focus:ring-green-600 outline-none" required minLength={6} />
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="border p-2 rounded focus:ring-2 focus:ring-green-600 outline-none">
              <option value={ROLE_ENVIRONMENTAL_DISTRICT_OFFICER}>ESMP Writer</option>
              <option value={ROLE_REVIEWER}>Reviewer</option>
              <option value={ROLE_VIEWER}>Viewer (Read Only)</option>
              <option value={ROLE_ADMIN}>Admin</option>
            </select>
            {DISTRICT_EDO_ROLES.includes(form.role) && (
              <input
                type="text"
                placeholder="District (e.g., Lilongwe)"
                value={form.district}
                onChange={e => setForm({ ...form, district: e.target.value })}
                className="border p-2 rounded focus:ring-2 focus:ring-green-600 outline-none"
              />
            )}
          </div>

          <button type="submit" disabled={loading} className={`mt-4 px-6 py-2 rounded text-white font-semibold ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-700 hover:bg-green-800"}`}>
            {loading ? "Adding User..." : "Add User"}
          </button>
        </form>

        {/* Search */}
        <div className="bg-white p-4 rounded-xl shadow mb-6">
          <input type="text" placeholder="Search users by name or email..." value={search} onChange={e => setSearch(e.target.value)} className="border p-2 rounded w-full focus:ring-2 focus:ring-green-600 outline-none" />
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-green-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-green-700 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-green-700 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-green-700 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-green-700 uppercase">District</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-green-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-8 text-gray-500">No users found</td>
                  </tr>
                ) : (
                  filteredUsers.map(u => (
                    <tr key={u.id} className="hover:bg-green-50 transition">
                      <td className="px-4 py-3">{u.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                      <td className="px-4 py-3">
                        <select value={normalizeRole(u.role)} onChange={e => updateRole(u.id, e.target.value)} className="border p-1 rounded text-sm focus:ring-2 focus:ring-green-600 outline-none">
                          <option value={ROLE_ADMIN}>Admin</option>
                          <option value={ROLE_REVIEWER}>Reviewer</option>
                          <option value={ROLE_VIEWER}>Viewer</option>
                          <option value={ROLE_ENVIRONMENTAL_DISTRICT_OFFICER}>ESMP Writer</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{u.district || 'N/A'}</td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => deleteUser(u)} className="bg-red-500 hover:bg-red-600 text-white px-4 py-1 rounded text-sm font-semibold transition">Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <ConfirmDialog
        open={!!userToDelete}
        title="Confirm User Deletion"
        message={`Are you sure you want to delete ${userToDelete?.name || "this user"} from the system?`}
        confirmText="Delete User"
        cancelText="Cancel"
        destructive
        onConfirm={confirmDeleteUser}
        onCancel={() => setUserToDelete(null)}
      />
    </div>
  );
}
